import {
    RequiredValidationRule,
    PatternValidationRule,
    MaxLengthValidationRule,
    MinLengthValidationRule,
    MinValidationRule,
    MaxValidationRule,
    CustomValidationRule,
    AsyncCustomValidationRule,
    PrecisionValidationRule,
    ValidationRuleType,
} from '../models/validator-models';
import { ValidatorIdentifier } from '../models/validator-identifier';

/**
 * Creates a required field validator
 *
 * @example
 * TextField({
 *   validators: [required({ message: 'This field is required' })]
 * })
 */
export function required({ message }: { message?: string | string[] } = {}): RequiredValidationRule {
    return {
        type: ValidationRuleType.Required,
        message,
    };
}

/**
 * Creates a regex pattern validator
 *
 * @example
 * TextField({
 *   validators: [pattern({
 *     value: /^[A-Z]+$/,
 *     message: 'Must be uppercase letters'
 *   })]
 * })
 */
export function pattern({
    value,
    message,
}: {
    value: RegExp | string;
    message?: string | string[];
}): PatternValidationRule {
    return {
        type: ValidationRuleType.Pattern,
        value,
        message,
    };
}

/**
 * Creates a maximum length validator
 *
 * @example
 * TextField({
 *   validators: [maxLength({
 *     value: 50,
 *     message: 'Maximum 50 characters allowed'
 *   })]
 * })
 */
export function maxLength({ value, message }: { value: number; message?: string | string[] }): MaxLengthValidationRule {
    return {
        type: ValidationRuleType.MaxLength,
        value,
        message,
    };
}

/**
 * Creates a minimum length validator
 *
 * @example
 * TextField({
 *   validators: [minLength({
 *     value: 3,
 *     message: 'Please enter at least 3 characters'
 *   })]
 * })
 */
export function minLength({ value, message }: { value: number; message?: string | string[] }): MinLengthValidationRule {
    return {
        type: ValidationRuleType.MinLength,
        value,
        message,
    };
}

/**
 * Creates a minimum value validator (for numeric fields)
 *
 * @example
 * NumberField({
 *   validators: [min({
 *     value: 0,
 *     message: 'Value must be 0 or greater'
 *   })]
 * })
 */
export function min({ value, message }: { value: number; message?: string | string[] }): MinValidationRule {
    return {
        type: ValidationRuleType.Min,
        value,
        message,
    };
}

/**
 * Creates a maximum value validator (for numeric fields)
 *
 * @example
 * NumberField({
 *   validators: [max({
 *     value: 100,
 *     message: 'Value cannot exceed 100'
 *   })]
 * })
 */
export function max({ value, message }: { value: number; message?: string | string[] }): MaxValidationRule {
    return {
        type: ValidationRuleType.Max,
        value,
        message,
    };
}

/**
 * Creates a custom validator that uses a predefined identifier
 *
 * @example
 * TextField({
 *   validators: [custom({
 *     identifier: ValidatorIdentifier.UniqueLocationName,
 *     message: 'This location name already exists'
 *   })]
 * })
 */
export function custom({
    identifier,
    message,
}: {
    identifier: ValidatorIdentifier;
    message?: string | string[];
}): CustomValidationRule {
    return {
        type: ValidationRuleType.Custom,
        identifier,
        message,
    };
}

/**
 * Creates an async custom validator that uses a predefined identifier
 *
 * @example
 * TextField({
 *   validators: [asyncCustom({
 *     identifier: ValidatorIdentifier.UniqueSerialNumber,
 *     message: 'This serial number is already in use'
 *   })]
 * })
 */
export function asyncCustom({
    identifier,
    message,
}: {
    identifier: ValidatorIdentifier;
    message?: string | string[];
}): AsyncCustomValidationRule {
    return {
        type: ValidationRuleType.AsyncCustom,
        identifier,
        message,
    };
}

/**
 * Creates a precision validator for decimal places
 *
 * @example
 * NumberField({
 *   validators: [precision({
 *     value: 2,
 *     message: 'Maximum 2 decimal places allowed'
 *   })]
 * })
 */
export function precision({ value, message }: { value: number; message?: string | string[] }): PrecisionValidationRule {
    return {
        type: ValidationRuleType.Precision,
        value,
        message,
    };
}
